import { useState } from "react";
import { Link } from "react-router-dom";
import { Star, BadgeCheck, ArrowRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { HOME_REVIEWS, GOOGLE_RATING } from "@/data/reviews";

export const Stars = ({ size = 15 }) => (
  <div className="flex items-center gap-1" role="img" aria-label="Rated 5 out of 5 stars">
    {[0, 1, 2, 3, 4].map((i) => (
      <Star key={i} size={size} className="fill-[#d4af37] text-[#d4af37]" strokeWidth={0} />
    ))}
  </div>
);

export const GoogleTag = () => (
  <span className="inline-flex items-center gap-1.5 mono text-[9px] uppercase tracking-[0.25em] text-[#737373]">
    <BadgeCheck size={13} className="text-[#d4af37]" /> Verified Google Review
  </span>
);

export const ReviewCard = ({ review, expandable = false, testid }) => {
  const [expanded, setExpanded] = useState(false);
  const LIMIT = 240;
  const isLong = review.text.length > LIMIT;
  const shown = expandable && isLong && !expanded ? `${review.text.slice(0, LIMIT).trimEnd()}…` : review.text;

  return (
    <figure
      data-testid={testid}
      className="group h-full flex flex-col bg-[#0a0a0a] border border-white/10 rounded-sm p-8 lg:p-10 transition-[transform,border-color,box-shadow] duration-500 hover:-translate-y-1 hover:border-[#d4af37]/45 hover:shadow-[0_24px_60px_rgba(0,0,0,0.5)]"
    >
      <div className="flex items-center justify-between mb-6">
        <Stars />
        <GoogleTag />
      </div>
      <blockquote className="text-[#f4f0ea]/85 text-base leading-relaxed tracking-wide flex-1">
        {shown}
      </blockquote>
      {expandable && isLong && (
        <button
          onClick={() => setExpanded((v) => !v)}
          data-testid={testid ? `${testid}-toggle` : undefined}
          className="mono text-[10px] uppercase tracking-[0.2em] text-[#d4af37] hover:text-[#f0d57d] mt-5 self-start transition-colors duration-300"
        >
          {expanded ? "Read less" : "Read more"}
        </button>
      )}
      <figcaption className="mt-8 pt-6 border-t border-white/10 text-[#f4f0ea]">{review.name}</figcaption>
    </figure>
  );
};

// Homepage section — understated social proof after "Coverages".
export const HomeReviews = () => (
  <section id="client-experiences" data-testid="home-reviews" className="relative bg-[#030303] py-20 lg:py-28 border-t border-white/10">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
      <div className="flex items-center gap-3 mb-8">
        <span className="mono text-[10px] uppercase tracking-[0.35em] text-[#d4af37]">Client Experiences</span>
        <span className="h-px flex-1 bg-white/10" />
        <span className="hidden sm:flex items-center gap-2 mono text-[10px] uppercase tracking-[0.3em] text-[#737373]">
          <Stars size={12} /> {GOOGLE_RATING} Google Rating
        </span>
      </div>

      <Reveal className="mb-4 max-w-3xl">
        <h2 className="serif font-light leading-[1.02] text-[#f4f0ea] text-4xl sm:text-5xl lg:text-6xl">
          What our <span className="italic text-[#d4af37]">clients say</span>.
        </h2>
      </Reveal>
      <Reveal delay={0.1} className="mb-14 max-w-2xl">
        <p className="text-[#a3a3a3] text-base leading-relaxed tracking-wide">
          Real experiences from families who trusted John Campbell Insurance to help protect what matters most.
        </p>
      </Reveal>

      <div className="grid md:grid-cols-3 gap-6">
        {HOME_REVIEWS.map((r, i) => (
          <Reveal key={r.id} delay={i * 0.08}>
            <ReviewCard review={r} expandable testid={`home-review-${r.id}`} />
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.1}>
        <div className="mt-14 flex justify-center">
          <Link
            to="/client-experiences"
            data-testid="home-reviews-cta"
            className="group inline-flex items-center gap-3 mono text-xs uppercase tracking-[0.2em] px-8 py-4 border border-[#d4af37]/50 text-[#d4af37] rounded-full hover:bg-[#d4af37] hover:text-[#030303] transition-colors duration-400"
          >
            Read More Client Experiences
            <ArrowRight size={15} className="transition-transform duration-400 group-hover:translate-x-1" />
          </Link>
        </div>
      </Reveal>
    </div>
  </section>
);
